import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid, GridItem, Heading, StackDivider, Text, VStack } from '@chakra-ui/react';
import { CodeTextarea, FormInput } from '@netiva/classifieds-ui';

import { ApiBaseUrl } from '@/environment';
import { useAppSelector } from '@/store';
import { SdkParams } from '@/store/sdk';
import { getDivCode } from '../utils';
import { CommonFormControls } from './CommonFormControls';

export type CarouselConfigurationProps = {
  loaderScriptCode: string;
};

export const CarouselConfiguration: FC<CarouselConfigurationProps> = ({ loaderScriptCode }) => {
  const { t } = useTranslation();

  const { filter, params, theme } = useAppSelector((state) => state.sdk);

  const integrationCode = useMemo(() => {
    if (!params.platform) {
      return '';
    }
    return getDivCode({
      apiUrl: ApiBaseUrl,
      component: 'carousel',
      type: 'direct',
      filter: filter ? JSON.stringify(filter) : '',
      theme: theme ? JSON.stringify(theme) : '',
      language: params.language,
      platform: params.platform,
      paramCount: params.count?.toString(),
      recaptcha: 'false',
    });
  }, [filter, params, theme]);

  return (
    <VStack align="stretch" divider={<StackDivider />} spacing={8}>
      <div>
        <Heading fontSize="lg" my={2}>
          {t('sdk.carousel.title')}
        </Heading>
        <VStack align="stretch" spacing={4}>
          <CommonFormControls />
          <FormInput<SdkParams>
            label={t('sdk.carousel.count.label')}
            description={t('sdk.carousel.count.helpText')}
            name="count"
          />
        </VStack>
      </div>
      <div>
        <Heading fontSize="lg" my={2}>
          {t('sdk.code.title')}
        </Heading>
        <Grid templateColumns={{ base: '1fr', lg: 'minmax(0,1fr) auto minmax(0,1fr)' }} gap={4}>
          <GridItem>
            <div>
              <Text mb={2}>{t('sdk.scriptCode.helpText')}</Text>
              <CodeTextarea value={loaderScriptCode} minHeight="60px" />
            </div>
            <Box mt={4}>
              <Text mb={2}>{t('sdk.divCode.helpText')}</Text>
              <CodeTextarea value={integrationCode} minHeight="185px" />
            </Box>
          </GridItem>
          <GridItem display={{ base: 'none', lg: 'unset' }}>
            <Divider orientation="vertical" />
          </GridItem>
          <GridItem>
            <Text mb={2}>{t('sdk.preview')}</Text>
            <Box dangerouslySetInnerHTML={{ __html: integrationCode }} width="full" border="default" padding={2}></Box>
          </GridItem>
        </Grid>
      </div>
    </VStack>
  );
};
